import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
	Sparkles,
	Calendar,
	FileText,
	Video,
	FileDown,
	Globe,
	Users,
	Route,
	BarChart3,
	Layout,
	Share2,
	Bookmark,
	Layers,
	ClipboardList,
	BookOpen,
	TrendingUp,
	UserCheck,
	Map,
	Megaphone,
	Code2,
	Upload,
	Wand2,
	PenLine,
	Tag,
	Monitor,
	Hotel,
	Palette,
	FolderOpen,
	UsersRound,
	CircleDollarSign,
	LucideIcon,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface SmallFeature {
	icon: LucideIcon;
	label: string;
	description: string;
	featuredIndex: number;
	comingSoon?: boolean;
}

interface FeaturedTileData {
	icon: LucideIcon;
	label: string;
	description: string;
	color: string;
	bg: string;
	border: string;
}

interface HoveredInfo {
	icon: LucideIcon;
	label: string;
	description: string;
	featuredIndex: number;
}

// ── Data ──────────────────────────────────────────────────────────────────────

// featuredIndex:  0 = Trip Building  |  1 = Proposals & Quotes
//                 2 = Clients & CRM  |  3 = Marketing

const smallFeatures: SmallFeature[] = [
	// ── Trip Building (0) ── wraps the top-left corner ───────────────────────
	// Row 1, cols 3–4 (right of FT0):
	{ icon: Route, label: "Route Builder", description: "Drag-and-drop routes with live map previews built right in.", featuredIndex: 0 },
	{ icon: Layers, label: "Day Planner", description: "Plan each day hour-by-hour with flexible time blocks.", featuredIndex: 0 },
	// Row 2, cols 1–4 (below FT0):
	{ icon: Layout, label: "Templates", description: "Start any itinerary in seconds with gorgeous pre-built templates.", featuredIndex: 0 },
	{ icon: Users, label: "Collaboration", description: "Invite team members to co-edit any trip in real time.", featuredIndex: 0 },
	{ icon: Calendar, label: "Calendar", description: "Visualise every trip day-by-day in a clean calendar layout.", featuredIndex: 0 },
	{ icon: BookOpen, label: "Content Library", description: "Save and reuse your best content, photos, and place descriptions.", featuredIndex: 0 },
	// Row 3, col 4 (above FT0 row / middle):
	{ icon: FolderOpen, label: "Trip Documents", description: "Store and share all your trip documents in one place: visas, tickets and vouchers.", featuredIndex: 0, comingSoon: true },
	// Row 2 col 5 (right area below FT0):
	{ icon: Hotel, label: "Bookings", description: "Search and book hotels for your clients directly within Stippl.", featuredIndex: 0, comingSoon: true },

	// ── Proposals & Quotes (1) ── wraps the top-right corner ─────────────────
	// Row 1, cols 5–6 (left of FT1):
	{ icon: ClipboardList, label: "Booking Requests", description: "Receive and manage client booking requests in one place.", featuredIndex: 1 },
	{ icon: Tag, label: "Pricing", description: "Build itemised pricing and quotes directly inside proposals.", featuredIndex: 1 },
	// Row 2, cols 6–8 (below FT1):
	{ icon: FileDown, label: "PDF Export", description: "One-click export to pixel-perfect, branded PDF proposals.", featuredIndex: 1 },
	{ icon: FileText, label: "Digital Proposal", description: "Interactive digital proposals clients can view and approve online.", featuredIndex: 1 },
	{ icon: Palette, label: "Branding", description: "Your logo, colours and style applied everywhere automatically.", featuredIndex: 1 },

	// ── Marketing (3) ── wraps the bottom-left corner ────────────────────────
	// Row 3, cols 1–3 (above FT3):
	{ icon: Video, label: "Travel Reels", description: "Auto-generate destination reels to wow your clients.", featuredIndex: 3 },
	{ icon: Code2, label: "Map Embed", description: "Embed beautiful interactive maps directly on your own website.", featuredIndex: 3 },
	{ icon: Monitor, label: "Website Builder", description: "Build a stunning travel website without writing a line of code.", featuredIndex: 3, comingSoon: true },
	// Row 4, cols 3–4 (right of FT3):
	{ icon: Share2, label: "Sharable Trip Page", description: "Share any itinerary via a beautiful, branded public link.", featuredIndex: 3 },
	{ icon: BarChart3, label: "Analytics", description: "Track opens, views, and engagement on every proposal sent.", featuredIndex: 3, comingSoon: true },

	// ── Clients & CRM (2) ── wraps the bottom-right corner ───────────────────
	// Row 3, cols 7–8 (above FT2):
	{ icon: TrendingUp, label: "Sales Pipeline", description: "Track every lead and deal through your sales funnel.", featuredIndex: 2 },
	{ icon: UserCheck, label: "Contact Management", description: "Store and manage all your client details in one place.", featuredIndex: 2 },
	// Row 4, cols 5–6 (left of FT2):
	{ icon: Bookmark, label: "Trip Library", description: "Store all past and current trips in a searchable library.", featuredIndex: 2 },
	{ icon: Globe, label: "Language Translation", description: "Auto-translate itineraries for international clients.", featuredIndex: 2, comingSoon: true },

	// ── Group Trips (0) ── Row 3 col 5, linked to Trip Building ──────────────
	{ icon: UsersRound, label: "Group Trips", description: "Coordinate group travel and shared itineraries effortlessly.", featuredIndex: 0, comingSoon: true },

	// ── Revenue Dashboard (2) ── Row 3 col 6 ─────────────────────────────────
	{ icon: CircleDollarSign, label: "Revenue Dashboard", description: "Track earnings, bookings, and revenue trends at a glance.", featuredIndex: 2, comingSoon: true },
];

const featuredTiles: FeaturedTileData[] = [
	{
		icon: Map,
		label: "Trip Building",
		description: "Build complete trip itineraries day-by-day with ease.",
		color: "#7C3AED",
		bg: "linear-gradient(135deg, rgba(124,58,237,0.13) 0%, rgba(124,58,237,0.04) 100%)",
		border: "rgba(124,58,237,0.3)",
	},
	{
		icon: FileDown,
		label: "Proposals & Quotes",
		description: "Branded, beautiful proposals ready to send in seconds.",
		color: "#00D4AA",
		bg: "linear-gradient(135deg, rgba(0,212,170,0.13) 0%, rgba(0,212,170,0.04) 100%)",
		border: "rgba(0,212,170,0.3)",
	},
	{
		icon: Users,
		label: "Clients & CRM",
		description: "Keep track of every client, proposal and booking in one place.",
		color: "#E879F9",
		bg: "linear-gradient(135deg, rgba(232,121,249,0.13) 0%, rgba(232,121,249,0.04) 100%)",
		border: "rgba(232,121,249,0.3)",
	},
	{
		icon: Megaphone,
		label: "Marketing",
		description: "Turn every itinerary into content.",
		color: "#818CF8",
		bg: "linear-gradient(135deg, rgba(129,140,248,0.13) 0%, rgba(129,140,248,0.04) 100%)",
		border: "rgba(129,140,248,0.3)",
	},
];

// Per-group active background (rgba with visible opacity ~14%)
const groupActiveBg: Record<number, string> = {
	0: "rgba(124,58,237,0.10)",
	1: "rgba(0,212,170,0.10)",
	2: "rgba(232,121,249,0.10)",
	3: "rgba(129,140,248,0.10)",
};

const aiFeatures = [
	{ icon: Upload, label: "AI Import", comingSoon: false },
	{ icon: Wand2, label: "Smart day suggestions", comingSoon: true },
	{ icon: PenLine, label: "Write descriptions", comingSoon: true },
];

// ── SmallTile ─────────────────────────────────────────────────────────────────

function SmallTile({
	feat,
	isLast,
	isActive,
	onHover,
}: {
	feat: SmallFeature;
	isLast: boolean;
	isActive: boolean;
	onHover: (feat: SmallFeature) => void;
}) {
	const Icon = feat.icon;
	const activeColor = featuredTiles[feat.featuredIndex]?.color ?? "#8B5CF6";
	const activeBg = groupActiveBg[feat.featuredIndex] ?? "rgba(139,92,246,0.10)";

	return (
		<div
			className="relative flex flex-col items-center justify-center gap-2 cursor-default"
			style={{
				background: isActive ? activeBg : "#ffffff",
				transition: "background 0.2s",
				minHeight: 90,
				padding: 16,
			}}
			onMouseEnter={() => onHover(feat)}
		>
			{/* SOON badge */}
			{feat.comingSoon && (
				<div
					className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-widest uppercase"
					style={{
						color: "#9CA3AF",
						border: "1px solid #E5E7EB",
						lineHeight: 1.4,
					}}
				>
					Soon
				</div>
			)}

			{/* Icon */}
			<div
				className="w-6 h-6 flex items-center justify-center"
				style={{ color: isActive ? activeColor : "#9CA3AF", transition: "color 0.2s" }}
			>
				<Icon style={{ width: 22, height: 22 }} />
			</div>

			{/* Label */}
			<span
				className="text-center leading-tight"
				style={{
					fontSize: 11,
					fontWeight: 500,
					color: isActive ? "#0F3E4A" : "#6B7280",
					transition: "color 0.2s",
					maxWidth: 80,
				}}
			>
				{feat.label}
			</span>
		</div>
	);
}

// ── FeaturedTile ──────────────────────────────────────────────────────────────

function FeaturedTile({
	tile,
	hoveredInfo,
	isSelfHovered = false,
	onHover,
	onLeave,
}: {
	tile: FeaturedTileData;
	hoveredInfo: HoveredInfo | null;
	isSelfHovered?: boolean;
	onHover: () => void;
	onLeave: () => void;
}) {
	const isHighlighted = hoveredInfo !== null || isSelfHovered;

	// Extract rgb from color for dynamic background
	const colorMap: Record<string, string> = {
		"#7C3AED": "124,58,237",
		"#00D4AA": "0,212,170",
		"#E879F9": "232,121,249",
		"#818CF8": "129,140,248",
	};
	const rgb = colorMap[tile.color] ?? "124,58,237";

	return (
		<div
			className="col-span-2 flex items-center gap-4 px-5 py-5 cursor-pointer relative overflow-hidden"
			style={{
				background: isHighlighted
					? `linear-gradient(135deg, rgba(${rgb},0.22) 0%, rgba(${rgb},0.10) 100%)`
					: tile.bg || "#ffffff",
				transition: "background 0.25s",
			}}
			onMouseEnter={onHover}
			onMouseLeave={onLeave}
		>
			<AnimatePresence mode="wait">
				{!hoveredInfo && !isSelfHovered ? (
					<motion.div
						key="default"
						initial={{ opacity: 0, y: 6 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -6 }}
						transition={{ duration: 0.18 }}
						className="w-full"
					>
						<div className="w-6 h-1 rounded-full mb-3" style={{ background: tile.color, opacity: 0.7 }} />
						<p className="font-bold text-sm leading-snug" style={{ color: "#0F3E4A", fontFamily: "Poppins, sans-serif" }}>
							{tile.label}
						</p>
						<p className="text-xs leading-snug mt-1" style={{ color: "#6B7280" }}>
							{tile.description}
						</p>
					</motion.div>
				) : isSelfHovered && !hoveredInfo ? (
					<motion.div
						key="self-hovered"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.18 }}
						className="w-full"
					>
						{/* Icon commented out — causes layout jump (icon is 32px vs line 4px) */}
						<div className="w-6 h-1 rounded-full mb-3" style={{ background: tile.color }} />
						<p className="font-bold text-sm leading-snug" style={{ color: "#0F3E4A", fontFamily: "Poppins, sans-serif" }}>
							{tile.label}
						</p>
						<p className="text-xs leading-snug mt-1" style={{ color: "#6B7280" }}>
							{tile.description}
						</p>
					</motion.div>
				) : (
					<motion.div
						key={hoveredInfo!.label}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.18 }}
						className="w-full"
					>
						{/* Icon + category label commented out — causes layout jump */}
						<div className="w-6 h-1 rounded-full mb-3" style={{ background: tile.color }} />
						<p className="font-bold text-sm leading-snug" style={{ color: "#0F3E4A", fontFamily: "Poppins, sans-serif" }}>
							{hoveredInfo!.label}
						</p>
						<p className="text-xs leading-snug mt-1" style={{ color: "#6B7280" }}>
							{hoveredInfo!.description}
						</p>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

// ── AI Features band ─────────────────────────────────────────────────────────

function AIFeaturesBand({ compact }: { compact?: boolean }) {
	return (
		<div
			className={`${compact ? "px-4 py-3 gap-x-4 gap-y-2" : "px-8 py-5 gap-x-6 gap-y-3"} flex flex-wrap items-center`}
			style={{ background: "linear-gradient(135deg, #1E0A4A 0%, #160836 100%)" }}
		>
			<div className="flex items-center gap-2 flex-shrink-0">
				<Sparkles className="w-4 h-4" style={{ color: "#A78BFA" }} />
				<span className="text-xs font-bold tracking-widest uppercase" style={{ color: "#A78BFA" }}>
					AI Features
				</span>
			</div>
			<div className="w-px h-5 flex-shrink-0" style={{ background: "rgba(167,139,250,0.25)" }} />
			{aiFeatures.map((f) => {
				const Icon = f.icon;
				const Wrapper = compact ? "div" : motion.div;
				const hoverProps = compact ? {} : { whileHover: { backgroundColor: "rgba(167,139,250,0.2)" }, transition: { duration: 0.15 } };
				return (
					<Wrapper
						key={f.label}
						{...hoverProps}
						className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full${compact ? "" : " cursor-pointer"}`}
						style={{ background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)" }}
					>
						<Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#C084FC" }} />
						<span className="text-xs font-medium whitespace-nowrap" style={{ color: "rgba(255,255,255,0.85)" }}>
							{f.label}
						</span>
						{f.comingSoon && (
							<span
								className="text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded"
								style={{ background: "rgba(167,139,250,0.25)", color: "#C084FC" }}
							>
								Soon
							</span>
						)}
					</Wrapper>
				);
			})}
		</div>
	);
}

// ── Group small features by category ─────────────────────────────────────────

const featuresByCategory = featuredTiles.map((_, idx) => smallFeatures.filter((f) => f.featuredIndex === idx));

// ── Mobile category card ─────────────────────────────────────────────────────

function MobileCategoryCard({ tile, features }: { tile: FeaturedTileData; features: SmallFeature[] }) {
	return (
		<div className="bg-white">
			{/* Category header */}
			<div className="px-5 pt-5 pb-4">
				<div className="w-8 h-1 rounded-full mb-3" style={{ background: tile.color, opacity: 0.7 }} />
				<p className="font-bold text-base leading-snug" style={{ color: "#0F3E4A", fontFamily: "Poppins, sans-serif" }}>
					{tile.label}
				</p>
				<p className="text-xs leading-snug mt-1" style={{ color: "#6B7280" }}>
					{tile.description}
				</p>
			</div>

			{/* Sub-features grid */}
			<div className="grid grid-cols-3 border-t" style={{ borderColor: "#F3F4F6", gap: 1, background: "#F3F4F6" }}>
				{features.map((feat) => {
					const Icon = feat.icon;
					return (
						<div key={feat.label} className="relative flex flex-col items-center justify-center gap-1.5 bg-white" style={{ padding: 12, minHeight: 72 }}>
							{feat.comingSoon && (
								<div
									className="absolute top-1.5 right-1.5 px-1 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase"
									style={{ color: "#9CA3AF", border: "1px solid #E5E7EB", lineHeight: 1.4 }}
								>
									Soon
								</div>
							)}
							<Icon style={{ width: 18, height: 18, color: tile.color }} />
							<span className="text-center leading-tight" style={{ fontSize: 10, fontWeight: 500, color: "#0F3E4A" }}>
								{feat.label}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

export function FeaturesGrid() {
	const [hoveredInfo, setHoveredInfo] = useState<HoveredInfo | null>(null);
	const [hoveredFeaturedIndex, setHoveredFeaturedIndex] = useState<number | null>(null);

	const sf = smallFeatures;

	const handleHover = (feat: SmallFeature) =>
		setHoveredInfo({ icon: feat.icon, label: feat.label, description: feat.description, featuredIndex: feat.featuredIndex });
	const handleLeave = () => {
		setHoveredInfo(null);
		setHoveredFeaturedIndex(null);
	};

	const isSmallActive = (feat: SmallFeature) => hoveredInfo?.featuredIndex === feat.featuredIndex || hoveredFeaturedIndex === feat.featuredIndex;

	const S = (feat: SmallFeature, last = false) => (
		<SmallTile key={feat.label} feat={feat} isLast={last} isActive={isSmallActive(feat)} onHover={handleHover} />
	);

	const FT = (idx: number) => (
		<FeaturedTile
			tile={featuredTiles[idx]}
			hoveredInfo={hoveredInfo?.featuredIndex === idx ? hoveredInfo : null}
			isSelfHovered={hoveredFeaturedIndex === idx}
			onHover={() => {
				setHoveredFeaturedIndex(idx);
				setHoveredInfo(null);
			}}
			onLeave={() => {
				setHoveredFeaturedIndex(null);
				setHoveredInfo(null);
			}}
		/>
	);

	return (
		<section className="relative py-12 md:py-20 px-4 md:px-6" style={{ backgroundColor: "#FAFBFC" }}>
			<div className="max-w-[1160px] mx-auto">
				{/* Section header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="text-center mb-12"
				>
					<p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: "#8B5CF6" }}>
						EVERYTHING YOU NEED
					</p>
					<h2
						className="text-3xl md:text-5xl font-bold mb-4"
						style={{ fontFamily: "Poppins, sans-serif", color: "#0F3E4A" }}
					>
						One platform.<br />Every tool you need.
					</h2>
					<p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#6B7280" }}>
						Stippl combines every travel business tool into one seamless workspace, no more juggling apps.
					</p>
				</motion.div>

				{/* Mobile layout */}
				<motion.div
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.7, delay: 0.2 }}
					className="md:hidden rounded-3xl overflow-hidden border shadow-sm"
					style={{ background: "#F3F4F6", borderColor: "#E5E7EB" }}
				>
					{/* Order matches desktop visual flow: Trip Building, Proposals, Marketing, CRM */}
					<div style={{ display: "flex", flexDirection: "column", gap: 1, background: "#F3F4F6" }}>
						{[0, 1, 3, 2].map((idx) => (
							<MobileCategoryCard key={idx} tile={featuredTiles[idx]} features={featuresByCategory[idx]} />
						))}
					</div>

					<AIFeaturesBand compact />
				</motion.div>

				{/* Desktop grid */}
				<motion.div
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.7, delay: 0.2 }}
					className="hidden md:block rounded-3xl overflow-hidden border shadow-sm"
					style={{ background: "#F3F4F6", borderColor: "#E5E7EB" }}
					onMouseLeave={handleLeave}
				>
					{/*
            Layout (8 cols):
            Row 1: [FT0 Trip ×2] [T][T] [P][P] [FT1 Proposals ×2]
            Row 2: [T][T][T][T]  [T][P][P][P]
            Row 3: [M][M][M][T]  [G][·] [C][C]
            Row 4: [FT3 Marketing ×2] [M][M] [C][C] [FT2 CRM ×2]
          */}

					<div className="grid grid-cols-8" style={{ gap: 1 }}>
						{/* Row 1 */}
						{FT(0)}
						{S(sf[0])} {S(sf[1])}
						{S(sf[8])} {S(sf[9])}
						{FT(1)}

						{/* Row 2 */}
						{S(sf[2])} {S(sf[3])} {S(sf[4])} {S(sf[5])} {S(sf[7])}
						{S(sf[12])} {S(sf[10])} {S(sf[11], true)}

						{/* Row 3 */}
						{S(sf[13])} {S(sf[14])} {S(sf[15])} {S(sf[6])}
						{S(sf[22])} {S(sf[23])}
						{S(sf[18])} {S(sf[19], true)}

						{/* Row 4 */}
						{FT(3)}
						{S(sf[16])} {S(sf[17])}
						{S(sf[20])} {S(sf[21])}
						{FT(2)}
					</div>

					<AIFeaturesBand />
				</motion.div>
			</div>
		</section>
	);
}
